import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import ChartBarIcon from '@heroicons/react/outline/ChartBarIcon';
import DuplicateIcon from '@heroicons/react/outline/DuplicateIcon';
import LogoutIcon from '@heroicons/react/outline/LogoutIcon';
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon';
import ShareIcon from '@heroicons/react/outline/ShareIcon';
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon';
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon';
import UsersIcon from '@heroicons/react/outline/UsersIcon';
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon';
import { useDecision } from '@optimizely/react-sdk';
import { useLocation } from 'react-router';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { NetworkVisibility, SpaceMembershipStatus, } from '@tribeplatform/gql-client/types';
import { useAuthMember, useAuthToken, useLeaveSpace, } from '@tribeplatform/react-sdk/hooks';
import { SpaceAnalyticsModal } from '../../Analytics/SpaceAnalytics';
import { AuditLogsModal } from '../../AuditLog';
import { FeaturesList } from '../../common/constants/features';
import { useI18n } from '../../i18n';
import { SpaceMembersModal } from '../../MemberList/SpaceMembersModal';
import { ShareModal } from '../../ShareModal';
import { CreateSpaceTemplate } from '../CreateSpaceTemplate';
export const useSpaceActionItems = ({ space }) => {
    var _a;
    const intl = useI18n();
    const { isGuest } = useAuthMember();
    const [auditLogsModal, setAuditLogsModal] = useState(false);
    const [spaceAnalyticsModal, setSpaceAnalyticsModal] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [createSpaceTemplate, setCreateSpaceTemplate] = useState(false);
    const [spaceMembersModal, setSpaceMemberModal] = useState(false);
    const [decisionCreateSpaceTemplate] = useDecision(FeaturesList.CreateSpaceTemplate);
    const actionItems = [];
    const { mutate: leave } = useLeaveSpace();
    const joined = ((_a = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _a === void 0 ? void 0 : _a.membershipStatus) === SpaceMembershipStatus.joined;
    const { data: { network }, } = useAuthToken();
    const [canUpdateNetwork, canCreateTemplate] = hasScopesPermission(network, [
        'updateNetwork',
        'createTemplate',
    ]);
    const [canUpdateSpace, canAddSpaceMembers] = hasScopesPermission(space, [
        'updateSpace',
        'addSpaceMembers',
    ]);
    const itemCategoryText = intl.$t({
        id: 'Space.UseSpaceActionItems.CategoryText',
        defaultMessage: '{SPACE_CC}',
    });
    const { pathname } = useLocation();
    const spacePath = space === null || space === void 0 ? void 0 : space.relativeUrl;
    const isSpacePage = pathname === spacePath;
    if (!(space === null || space === void 0 ? void 0 : space.id) || isGuest)
        return { actions: [], children: null };
    if (!isSpacePage)
        actionItems.push({
            url: spacePath,
            icon: ViewGridIcon,
            name: intl.$t({
                id: 'Space.Actions.View',
                defaultMessage: 'View {SPACE}',
            }),
            category: itemCategoryText,
        });
    actionItems.push({
        action: () => setShareModal(true),
        icon: ShareIcon,
        name: intl.$t({
            id: 'Space.UseSpaceActionItems.Items.ShareSpace',
            defaultMessage: 'Share {SPACE}',
        }),
        category: itemCategoryText,
        hotkey: 'option+s,alt+s',
    });
    if (canAddSpaceMembers) {
        actionItems.push({
            action: () => setSpaceMemberModal(true),
            icon: UsersIcon,
            name: 'View members',
            category: 'Space',
        });
    }
    if (canUpdateSpace)
        actionItems.push({
            url: `/settings/moderation/${space.id}/pending-posts`,
            icon: ShieldCheckIcon,
            name: intl.$t({
                id: 'Generics.Moderation',
                defaultMessage: 'Moderation',
            }),
            category: itemCategoryText,
            hotkey: 'm',
        });
    if (canUpdateSpace)
        actionItems.push({
            action: () => setSpaceAnalyticsModal(true),
            icon: ChartBarIcon,
            name: intl.$t({
                id: 'Space.UseSpaceActionItems.Items.SpaceAnalytics',
                defaultMessage: '{SPACE_CC} analytics',
            }),
            hotkey: 'option+a,alt+a',
            category: itemCategoryText,
        });
    if (canUpdateSpace && canUpdateNetwork)
        actionItems.push({
            action: () => setAuditLogsModal(true),
            icon: ShieldExclamationIcon,
            name: intl.$t({
                id: 'Generics.AuditLogs',
                defaultMessage: 'Audit logs',
            }),
            hotkey: 'option+l,alt+l',
            category: itemCategoryText,
        });
    if (canUpdateSpace && canUpdateNetwork)
        actionItems.push({
            url: `/manage/customizer/spaces/${space.id}/blocks`,
            icon: PencilAltIcon,
            name: intl.$t({
                id: 'Generics.Customize',
                defaultMessage: 'Customize',
            }),
            category: itemCategoryText,
            hotkey: 'c',
        });
    if (canCreateTemplate && (decisionCreateSpaceTemplate === null || decisionCreateSpaceTemplate === void 0 ? void 0 : decisionCreateSpaceTemplate.enabled))
        actionItems.push({
            action: () => setCreateSpaceTemplate(true),
            icon: DuplicateIcon,
            name: intl.$t({
                id: 'Space.Actions.CreateTemplate',
                defaultMessage: 'Create template',
            }),
            category: itemCategoryText,
            hotkey: 'alt+t',
        });
    if (joined)
        actionItems.push({
            action: () => leave({ spaceId: space === null || space === void 0 ? void 0 : space.id }),
            icon: LogoutIcon,
            name: intl.$t({
                id: 'Space.UseSpaceActionItems.Items.LeaveSpace',
                defaultMessage: 'Leave {SPACE}',
            }),
            category: itemCategoryText,
            hotkey: 'j',
        });
    const children = (_jsxs(_Fragment, { children: [shareModal && (_jsx(ShareModal, { isPrivate: (space === null || space === void 0 ? void 0 : space.hidden) || network.visibility === NetworkVisibility.private, spaceId: space === null || space === void 0 ? void 0 : space.id, open: true, onClose: () => setShareModal(false), title: space.name, url: space === null || space === void 0 ? void 0 : space.url }, void 0)), auditLogsModal && (_jsx(AuditLogsModal, { open: true, onClose: () => setAuditLogsModal(false), spaceId: space === null || space === void 0 ? void 0 : space.id }, void 0)), spaceAnalyticsModal && (_jsx(SpaceAnalyticsModal, { open: true, onClose: () => setSpaceAnalyticsModal(false), space: space, linkToContent: false }, void 0)), createSpaceTemplate && (_jsx(CreateSpaceTemplate, { open: true, onClose: () => setCreateSpaceTemplate(false), space: space }, void 0)), spaceMembersModal && (_jsx(SpaceMembersModal, { open: true, onClose: () => setSpaceMemberModal(false), space: space }, void 0))] }, void 0));
    return { actions: actionItems, children };
};
//# sourceMappingURL=useSpaceActionItems.js.map